import { z } from 'zod'

export const MessageRoleSchema = z.enum(['user', 'assistant'])
export type MessageRole = z.infer<typeof MessageRoleSchema>

export const StoredMessageSchema = z.object({
  role: MessageRoleSchema,
  content: z.string(),
})
export type StoredMessage = z.infer<typeof StoredMessageSchema>

export const ChatRecordSchema = z.object({
  id: z.string(),
  chat_id: z.string(),
  user_id: z.string(),
  messages: z.array(StoredMessageSchema),
  summary: z.string().nullable(),
  total_input_tokens: z.number(),
  total_output_tokens: z.number(),
  total_steps: z.number(),
  created_at: z.string(),
  updated_at: z.string(),
})
export type ChatRecord = z.infer<typeof ChatRecordSchema>

export const ToolCallSchema = z.object({
  toolCallId: z.string(),
  toolName: z.string(),
  input: z.record(z.unknown()),
  result: z.string().optional(),
  isError: z.boolean().optional(),
  status: z.enum(['running', 'complete']),
})
export type ToolCall = z.infer<typeof ToolCallSchema>

export const MessageSchema = z.object({
  id: z.string(),
  role: MessageRoleSchema,
  content: z.string(),
  reasoning: z.string(),
  toolCalls: z.array(ToolCallSchema),
  isStreaming: z.boolean(),
})
export type Message = z.infer<typeof MessageSchema>

// Discriminated union covers all event types sent by the agent SSE endpoint.
// safeParse is used when reading the stream — unrecognised events are silently dropped.
export const SseEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('start') }),
  z.object({ type: z.literal('text-start') }),
  z.object({ type: z.literal('text-delta'), delta: z.string() }),
  z.object({ type: z.literal('text-end') }),
  z.object({ type: z.literal('reasoning-start') }),
  z.object({ type: z.literal('reasoning-delta'), delta: z.string() }),
  z.object({ type: z.literal('reasoning-end') }),
  z.object({
    type: z.literal('tool-call-start'),
    toolCallId: z.string(),
    toolName: z.string(),
    input: z.record(z.unknown()).default({}),
  }),
  z.object({
    type: z.literal('tool-call-result'),
    toolCallId: z.string(),
    toolName: z.string(),
    result: z.string(),
    isError: z.boolean().optional(),
  }),
  z.object({ type: z.literal('error'), message: z.string() }),
  z.object({ type: z.literal('finish'), finishReason: z.string() }),
])
export type SseEvent = z.infer<typeof SseEventSchema>

export const UsageSchema = z.object({
  period: z.string(),
  totals: z.object({
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number(),
    cacheCreationTokens: z.number(),
  }),
  estimatedCostUsd: z.number(),
})

export type Usage = z.infer<typeof UsageSchema>
